import { useState, useCallback, useEffect } from "react";
import { DateTime } from "luxon"

import { APIError, useAxios } from "../core/_axios";
import { Account, UserAccountMembership } from "../core/_models";
import { managementGetAccountUsersByUniqueIdentifier, managementGetAllUsers } from "../core/_requests";
import LoadingSpinner from "./LoadingSpinner";
import { Link } from "react-router-dom";
import { UserWithMembershipsFast } from "../core/_models_fast";
import { getCurrentAccountUsers } from "../core/_requests_v2";
import useLocalizedCopy from "../hooks/useLocalizedCopy";

export function UsersList({
    account = undefined,
    isForCurrentAccount = false
}: {
    account?: Account,
    isForCurrentAccount?: boolean
}) {

    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [userAccountMemberships, setUserAccountMemberships] = useState<UserAccountMembership[]>([]);
    const [totalUserAccountMemberships, usetTotalUserAccountMemberships] = useState<null | number>(null);

    const [usersWithMemberships, setUsersWithMemberships] = useState<UserWithMembershipsFast[]>([]);
    const [totalUsersWithMemberships, setTotalUsersWithMemberships] = useState<null | number>(null);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        setLoadingError(null);
        try {
            if (isForCurrentAccount) {
                const {
                    user_account_memberships: currentUserAccountMemberships,
                    user_account_memberships_count: currentUserAccountMembershipsCount
                } = await getCurrentAccountUsers(axios);
                setUserAccountMemberships(currentUserAccountMemberships);
                usetTotalUserAccountMemberships(currentUserAccountMembershipsCount);
            } else {
                if (account) {
                    const {
                        user_account_memberships: updatedUsers,
                        user_account_memberships_count: updatedUsersCount,
                    } = await managementGetAccountUsersByUniqueIdentifier(axios, account.unique_identifier);
                    setUserAccountMemberships(updatedUsers);
                    usetTotalUserAccountMemberships(updatedUsersCount);
                } else {
                    const {
                        users_with_memberships: updatedUsersWithMemberships,
                        users_with_memberships_count: updatedUsersWithMembershipsCount,
                    } = await managementGetAllUsers(axios);
                    setUsersWithMemberships(updatedUsersWithMemberships);
                    setTotalUsersWithMemberships(updatedUsersWithMembershipsCount);
                }
            }
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Errors.generic + '. ' + copy.Common.messages.tryAgain
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, account, isForCurrentAccount]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    if (loading) {
        return (
            <LoadingSpinner />
        );
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadUsers}>
                        {copy.Common.messages.tryAgain}
                    </button>
                </div>
            </div>
        );
    }

    if (account) {
        return <UserAccountMembershipsList
            account={account}
            userAccountMemberships={userAccountMemberships}
            totalUserAccountMemberships={totalUserAccountMemberships}
        />
    }

    return <UsersWithMembershipsList
        usersWithMemberships={usersWithMemberships}
        totalUsersWithMemberships={totalUsersWithMemberships}
    />

}

function UsersWithMembershipsList({
    usersWithMemberships,
    totalUsersWithMemberships
}: {
    usersWithMemberships: UserWithMembershipsFast[],
    totalUsersWithMemberships: number
}) {
    const { copy } = useLocalizedCopy();

    return <div className="grid gap-5 lg:gap-7.5">
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-5">
                <h3 className="card-title">
                    {copy.Account.users.title}{typeof totalUsersWithMemberships === 'number' ? ` (${totalUsersWithMemberships} ${copy.Common.labels.total})` : ''}
                </h3>
                {false && <div className="flex gap-6">
                    <div className="relative">
                        <i className="ki-filled ki-magnifier leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3">
                        </i>
                        <input className="input input-sm pl-8" data-datatable-search="#teams_table" placeholder={copy.Account.users.searchPlaceholder} type="text" />
                    </div>
                    <label className="switch switch-sm">
                        <input className="order-2" name="check" type="checkbox" value="1" />
                        <span className="switch-label order-1">
                            Only Active Groups
                        </span>
                    </label>
                </div>}
            </div>
            <div className="card-body">
                <div data-datatable="true" data-datatable-page-size="10" id="teams_table">
                    <div className="scrollable-x-auto">
                        <table className="table table-fixed table-border" data-datatable-table="true">
                            {usersWithMemberships.length > 0 && <thead>
                                <th>
                                    <span className="sort asc">
                                        <span className="sort-label">
                                            {copy.Common.labels.name}
                                        </span>
                                        {false && <span className="sort-icon">
                                        </span>}
                                    </span>
                                </th>
                                <th>
                                    {copy.Common.labels.status}
                                </th>
                                <th className="w-[200px]">
                                    <span className="sort">
                                        <span className="sort-label">
                                            {copy.Services.list.created}
                                        </span>
                                        {false && <span className="sort-icon">
                                        </span>}
                                    </span>
                                </th>
                            </thead>}
                            {usersWithMemberships.length > 0 ? <tbody>
                                {usersWithMemberships.map((usersWithMembership) => {
                                    return <>
                                        <tr key={usersWithMembership.user.unique_identifier}>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex flex-col justify-center gap-2">
                                                        <Link
                                                            to={`/management/users/${usersWithMembership.user.unique_identifier}`}
                                                            className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer"
                                                        >
                                                            {usersWithMembership.user.public_data.name || '(No Name)'}
                                                        </Link>
                                                        <div className="text-2sm text-gray-600">
                                                            {usersWithMembership.user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {usersWithMembership.user.is_super_super_user ? <>
                                                    <span className="badge badge-sm badge-primary">
                                                        {copy.Admin.title}
                                                    </span>
                                                </> : <>
                                                    {(usersWithMembership.user.belongs_to_agency && usersWithMembership.user.is_agency_admin) ? <>
                                                        <span className="badge badge-sm badge-primary">
                                                            {`${usersWithMembership.user.belongs_to_agency.name} Admin`}
                                                        </span>
                                                    </> : <>
                                                        <div className="flex flex-col gap-2">
                                                            {usersWithMembership.memberships.map((membership) => {
                                                                return <div key={membership.unique_identifier} className="flex flex-col gap-1.5">
                                                                    <Link
                                                                        to={`/management/accounts/${membership.account.unique_identifier}`}
                                                                        className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer"
                                                                    >
                                                                        {membership.account.name}
                                                                    </Link>
                                                                    <div className="text-2sm text-gray-600">
                                                                    </div>
                                                                </div>
                                                            })}
                                                        </div>
                                                    </>}
                                                </>}
                                            </td>
                                            <td>
                                                {DateTime.fromISO(usersWithMembership.user.inserted_at).toRelative()}
                                            </td>
                                        </tr>
                                    </>
                                })}
                            </tbody> : <tbody>
                                <tr>
                                    <td colSpan={1} className="text-center">
                                        <div>
                                            {copy.Account.users.noUsers}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>}
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
}

function UserAccountMembershipsList({
    account,
    userAccountMemberships,
    totalUserAccountMemberships
}: {
    account: Account,
    userAccountMemberships: UserAccountMembership[],
    totalUserAccountMemberships: number
}) {
    const { copy } = useLocalizedCopy();

    return <div className="grid gap-5 lg:gap-7.5">
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-5">
                <h3 className="card-title">
                    {`${account.name} ${copy.Account.users.title}${typeof totalUserAccountMemberships === 'number' ? ` (${totalUserAccountMemberships} ${copy.Common.labels.total})` : ''}`}
                </h3>
                {false && <div className="flex gap-6">
                    <div className="relative">
                        <i className="ki-filled ki-magnifier leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3">
                        </i>
                        <input className="input input-sm pl-8" data-datatable-search="#teams_table" placeholder={copy.Account.users.searchPlaceholder} type="text" />
                    </div>
                    <label className="switch switch-sm">
                        <input className="order-2" name="check" type="checkbox" value="1" />
                        <span className="switch-label order-1">
                            Only Active Groups
                        </span>
                    </label>
                </div>}
            </div>
            <div className="card-body">
                <div data-datatable="true" data-datatable-page-size="10" id="teams_table">
                    <div className="scrollable-x-auto">
                        <table className="table table-fixed table-border" data-datatable-table="true">
                            {userAccountMemberships.length > 0 && <thead>
                                <th>
                                    <span className="sort asc">
                                        <span className="sort-label">
                                            {copy.Common.labels.name}
                                        </span>
                                        {false && <span className="sort-icon">
                                        </span>}
                                    </span>
                                </th>
                                <th className="w-[200px]">
                                    <span className="sort">
                                        <span className="sort-label">
                                            {copy.Services.list.created}
                                        </span>
                                        {false && <span className="sort-icon">
                                        </span>}
                                    </span>
                                </th>
                            </thead>}
                            {userAccountMemberships.length > 0 ? <tbody>
                                {userAccountMemberships.map((userAccountMembership) => {
                                    return <>
                                        <tr key={userAccountMembership.unique_identifier}>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex flex-col justify-center gap-2">
                                                        <div className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer">
                                                            {userAccountMembership.user.public_data.name || '(No Name)'}
                                                        </div>
                                                        <div className="text-2sm text-gray-600">
                                                            {userAccountMembership.user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {DateTime.fromISO(userAccountMembership.inserted_at).toRelative()}
                                            </td>
                                        </tr>
                                    </>
                                })}
                            </tbody> : <tbody>
                                <tr>
                                    <td colSpan={1} className="text-center">
                                        <div>
                                            {copy.Account.users.noUsers}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>}
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
}